import {
  jsonHeadersWithToken,
  getApiToken,
  getApiGuestToken,
  parseJsonSafe,
  getErrorMessage,
} from '@/lib/constants'
import { safeLocalStorage } from '@/lib/safe-storage'
import { showAiCreditsRequired } from '@/lib/aiCredits'
import { DEFAULT_TAGLINE, SOURCE_KIND, SOURCE_STATUS } from './constants/chatbotConstants'
import { parsePaginatedListResponse, normalizePagination } from './lib/pagination'

function requiredEnv(key) {
  const value = import.meta.env[key]
  if (!value) throw new Error(`${key} is required`)
  return value
}

const BASE = requiredEnv('VITE_CHATBOT_API_URL').replace(/\/+$/, '')

function wsBase(workspaceId) {
  return `${BASE}/workspaces/${workspaceId}/chatbot`
}

function buildListQuery({ page = 1, limit = 10, search, kind, summary } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  })
  if (search?.trim()) params.set('search', search.trim())
  if (kind) params.set('kind', kind)
  if (summary) params.set('summary', 'true')
  return params.toString()
}

function toIso(value) {
  if (!value) return value
  if (value instanceof Date) return value.toISOString()
  return typeof value === 'string' ? value : String(value)
}

async function chatbotFetch(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...jsonHeadersWithToken(),
      ...options.headers,
    },
  })
  if (res.status === 204) return null
  const data = await parseJsonSafe(res)
  if (!res.ok) {
    const err = new Error(getErrorMessage(data, `Request failed (${res.status} ${res.statusText})`))
    err.status = res.status
    err.code = data?.code
    err.details = data
    showAiCreditsRequired(err)
    throw err
  }
  return data
}

// ─── Demo persistence (local only; not on server) ─────────────────────────

function demoSourcesKey(workspaceId, botId) {
  return `cd_chatbot_demo_sources_${workspaceId}_${botId}`
}

function readDemoSources(workspaceId, botId) {
  try {
    const raw = sessionStorage.getItem(demoSourcesKey(workspaceId, botId))
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function writeDemoSources(workspaceId, botId, list) {
  sessionStorage.setItem(demoSourcesKey(workspaceId, botId), JSON.stringify(list))
}

export function getChatbotCurrentUserId() {
  try {
    const raw = safeLocalStorage.getItem('user_data')
    if (raw) {
      const user = JSON.parse(raw)
      if (user?.id != null) return String(user.id)
      if (user?._id != null) return String(user._id)
    }
  } catch {
    /* ignore */
  }
  const uRI = safeLocalStorage.getItem('uRI')
  return uRI ? String(uRI) : 'dev'
}

export function isSameUserId(a, b) {
  return a != null && b != null && String(a) === String(b)
}

export function getChatbotAgentName() {
  try {
    const raw = safeLocalStorage.getItem('user_data')
    if (raw) {
      const user = JSON.parse(raw)
      if (user?.name?.trim()) return user.name.trim()
    }
  } catch {
    /* ignore */
  }
  return 'Teammate'
}

function entityId(row) {
  return row?.id || row?._id
}

function mapBot(bot) {
  if (!bot) return bot
  return {
    ...bot,
    id: entityId(bot),
    disabled: Boolean(bot.disabled),
    tagline: typeof bot.tagline === 'string' ? bot.tagline : DEFAULT_TAGLINE,
    allowedOrigins: Array.isArray(bot.allowedOrigins) ? bot.allowedOrigins : [],
    resolvedChatRetention: bot.resolvedChatRetention || '3m',
    createdAt: toIso(bot.createdAt),
    updatedAt: toIso(bot.updatedAt),
  }
}

function mapFaq(faq) {
  return {
    ...faq,
    id: entityId(faq),
    updatedAt: toIso(faq.updatedAt),
  }
}

function mapSource(source) {
  return {
    ...source,
    id: entityId(source),
    lastSyncedAt: source.lastSyncedAt ? toIso(source.lastSyncedAt) : null,
    createdAt: toIso(source.createdAt),
    updatedAt: toIso(source.updatedAt),
  }
}

/**
 * Backend: role + content. Dashboard: from + body.
 * Tool rows and empty assistant tool-call turns are omitted.
 */
function mapMessage(m) {
  if (!m) return null
  const createdAt = toIso(m.createdAt)
  const role = m.role === 'user' ? 'visitor' : m.role

  if (role === 'visitor') {
    const body = (m.content || '').trim()
    if (!body) return null
    return { id: m.id, from: 'visitor', body, createdAt }
  }

  if (role === 'assistant') {
    const body = (m.content || '').trim()
    if (!body) return null
    return { id: m.id, from: 'bot', body, createdAt }
  }

  if (role === 'agent') {
    const body = (m.content || '').trim()
    if (!body) return null
    return {
      id: m.id,
      from: 'agent',
      body,
      createdAt,
      agentName: m.agentName || null,
      agentId: m.agentId || null,
    }
  }

  return null
}

export function mapConversation(conv) {
  const id = entityId(conv)
  const serverMessages = (conv.messages || []).map(mapMessage).filter(Boolean)
  const visitor = conv.visitor || {}

  return {
    id,
    botId: conv.botId,
    visitorId: conv.visitorId ? String(conv.visitorId) : null,
    visitor: {
      name: visitor.name || 'Anonymous visitor',
      email: visitor.email || undefined,
      phone: visitor.phone || undefined,
      country: visitor.country || undefined,
      company: visitor.company || undefined,
    },
    status: conv.status,
    aiPaused: Boolean(conv.aiPaused),
    assigneeId: conv.assigneeId
      ? String(conv.assigneeId)
      : conv.assignee
        ? String(conv.assignee)
        : null,
    assigneeName: conv.assigneeName || null,
    assignedAt: conv.assignedAt ? toIso(conv.assignedAt) : null,
    assignedBy: conv.assignedBy || null,
    assignee: conv.assigneeName || conv.assigneeId || conv.assignee || null,
    handoffRequested: Boolean(conv.handoffRequested),
    handoffReason: conv.handoffReason || null,
    handedOffAt: conv.handedOffAt ? toIso(conv.handedOffAt) : null,
    lastMessageAt: toIso(conv.lastMessageAt),
    lastMessageBy: conv.lastMessageBy || null,
    unreadByAgent: Boolean(conv.unreadByAgent),
    unreadByVisitor: Boolean(conv.unreadByVisitor),
    messages: serverMessages,
    sourcePath: conv.sourcePath || null,
    createdAt: toIso(conv.createdAt),
  }
}

/** Parse SSE `data:` JSON lines from a conversation stream. */
async function consumeSseStream(response, onEvent) {
  if (!response.ok || !response.body) {
    const message = await getErrorMessage(
      await parseJsonSafe(response),
      `Stream failed (${response.status})`,
    )
    throw new Error(message)
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let reading = true
  while (reading) {
    const { value, done } = await reader.read()
    if (done) {
      reading = false
      break
    }
    buffer += decoder.decode(value, { stream: true })
    let idx
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const raw = buffer.slice(0, idx)
      buffer = buffer.slice(idx + 2)
      const dataLine = raw
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trim())
        .join('\n')
      if (!dataLine) continue
      try {
        onEvent(JSON.parse(dataLine))
      } catch {
        /* ignore */
      }
    }
  }
}

/**
 * Long-lived inbox SSE (message.created, status changes, etc.).
 * Returns `{ cancel }` — call on unmount or when switching conversations.
 */
export function subscribeConversationStream(workspaceId, botId, conversationId, onEvent) {
  const ac = new AbortController()
  const url = `${wsBase(workspaceId)}/bots/${botId}/conversations/${conversationId}/stream`
  const promise = (async () => {
    try {
      const res = await fetch(url, {
        headers: {
          ...jsonHeadersWithToken(),
          accept: 'text/event-stream',
        },
        signal: ac.signal,
      })
      await consumeSseStream(res, onEvent)
    } catch (err) {
      if (err?.name === 'AbortError') return
      throw err
    }
  })()
  return { cancel: () => ac.abort(), done: promise }
}

// ────────────────────────── Bots ──────────────────────────

export async function listBots(workspaceId) {
  const data = await chatbotFetch(`${wsBase(workspaceId)}/bots`)
  return (Array.isArray(data) ? data : []).map(mapBot)
}

export async function getBot(workspaceId, botId) {
  const data = await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}`)
  return mapBot(data)
}

// Origins where this bot's widget has actually loaded (heartbeat from /config). Used to show a
// project as truly "Live" — its published host appearing here confirms the embed is running.
export async function getBotSeen(workspaceId, botId) {
  const data = await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}/seen`)
  return Array.isArray(data?.seen) ? data.seen : []
}

export async function createBot(workspaceId, data) {
  const created = await chatbotFetch(`${wsBase(workspaceId)}/bots`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  })
  return mapBot(created)
}

export async function updateBot(workspaceId, botId, patch) {
  const updated = await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}`, {
    method: 'POST',
    body: JSON.stringify(patch),
  })
  return mapBot(updated)
}

export async function deleteBot(workspaceId, botId) {
  await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}`, { method: 'DELETE' })
  return { success: true }
}

export async function duplicateBot(workspaceId, botId) {
  const copy = await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
  return mapBot(copy)
}

// ────────────────────────── FAQs ──────────────────────────

export async function listFaqs(workspaceId, botId, { page = 1, limit = 10, search, summary } = {}) {
  const qs = buildListQuery({ page, limit, search, summary })
  const data = await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}/faqs?${qs}`)
  return parsePaginatedListResponse(data, { page, limit, mapRow: mapFaq })
}

export async function createFaq(workspaceId, botId, data) {
  const created = await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}/faqs`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return mapFaq(created)
}

export async function updateFaq(workspaceId, botId, id, patch) {
  const updated = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/faqs/${id}`,
    {
      method: 'POST',
      body: JSON.stringify(patch),
    },
  )
  return mapFaq(updated)
}

export async function deleteFaq(workspaceId, botId, id) {
  await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}/faqs/${id}`, {
    method: 'DELETE',
  })
  return { success: true }
}

// ────────────────────────── Training sources ──────────────────────────

export async function listSources(
  workspaceId,
  botId,
  { page = 1, limit = 10, search, kind, summary } = {},
) {
  const qs = buildListQuery({ page, limit, search, kind, summary })
  const data = await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}/sources?${qs}`)
  const parsed = parsePaginatedListResponse(data, { page, limit, mapRow: mapSource })
  const items = parsed.data
  const totalItems = parsed.pagination.totalItems

  return {
    data: items,
    pagination: normalizePagination(
      { ...parsed.pagination, totalItems },
      page,
      limit,
    ),
    summary: parsed.summary,
  }
}

export async function addWebsiteSource(workspaceId, botId, url) {
  const created = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/sources/website`,
    {
      method: 'POST',
      body: JSON.stringify({ url }),
    },
  )
  return mapSource(created)
}

export async function updateWebsiteSource(workspaceId, botId, id, url) {
  const updated = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/sources/${id}/website`,
    {
      method: 'POST',
      body: JSON.stringify({ url }),
    },
  )
  return mapSource(updated)
}

/** Multipart upload — queues async document extraction + indexing (does not block). */
export async function addDocumentSource(workspaceId, botId, file) {
  if (!(file instanceof File)) {
    throw new Error('Expected a File')
  }

  const form = new FormData()
  form.append('file', file, file.name)

  const headers = {}
  const token = getApiToken()
  const guestToken = getApiGuestToken()
  if (token) headers['x-access-token'] = token
  if (guestToken) headers['guest-token'] = guestToken

  const res = await fetch(`${wsBase(workspaceId)}/bots/${botId}/documents`, {
    method: 'POST',
    headers,
    body: form,
  })

  const data = await parseJsonSafe(res)
  if (!res.ok) {
    throw new Error(
      getErrorMessage(data, `Upload failed (${res.status} ${res.statusText})`),
    )
  }

  if (data.source) return mapSource(data.source)

  const sourceId = data.sourceId || data.documentId
  return mapSource({
    id: sourceId,
    _id: sourceId,
    botId,
    kind: SOURCE_KIND.DOCUMENT,
    title: file.name,
    sizeBytes: file.size,
    status: SOURCE_STATUS.QUEUED,
    pagesIndexed: 0,
    lastSyncedAt: null,
    url: null,
    mimeType: file.type || null,
  })
}

export async function deleteSource(workspaceId, botId, id) {
  if (String(id).startsWith('demo_src_')) {
    const demo = readDemoSources(workspaceId, botId).filter((s) => s.id !== id)
    writeDemoSources(workspaceId, botId, demo)
    return { success: true }
  }
  await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}/sources/${id}`, {
    method: 'DELETE',
  })
  return { success: true }
}

/** Re-queue website crawl (failed or sync again). */
export async function retryWebsiteSource(workspaceId, botId, sourceId) {
  if (String(sourceId).startsWith('demo_src_')) {
    const demo = readDemoSources(workspaceId, botId).map((s) =>
      s.id === sourceId
        ? {
            ...s,
            status: SOURCE_STATUS.QUEUED,
            failureReason: null,
            lastSyncedAt: null,
          }
        : s,
    )
    writeDemoSources(workspaceId, botId, demo)
    return mapSource(demo.find((s) => s.id === sourceId))
  }
  const updated = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/sources/${sourceId}/retry`,
    { method: 'POST', body: JSON.stringify({}) },
  )
  return mapSource(updated)
}

/** Re-queue document parsing / chunking. */
export async function retryDocumentSource(workspaceId, botId, sourceId) {
  if (String(sourceId).startsWith('demo_src_')) {
    return retryWebsiteSource(workspaceId, botId, sourceId)
  }
  const updated = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/sources/${sourceId}/retry`,
    { method: 'POST', body: JSON.stringify({}) },
  )
  return mapSource(updated)
}

/** Re-queue FAQ embedding. */
export async function retryFaq(workspaceId, botId, faqId) {
  const updated = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/faqs/${faqId}/retry`,
    { method: 'POST', body: JSON.stringify({}) },
  )
  return mapFaq(updated)
}

// ────────────────────────── Conversations ──────────────────────────

export async function listConversations(workspaceId, botId, filters = {}) {
  const params = new URLSearchParams()
  if (filters.startAt) params.set('startAt', filters.startAt)
  if (filters.endAt) params.set('endAt', filters.endAt)
  const qs = params.toString()
  const data = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/conversations${qs ? `?${qs}` : ''}`,
  )
  return (Array.isArray(data) ? data : []).map(mapConversation)
}

export async function getConversation(workspaceId, botId, id) {
  const data = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/conversations/${id}`,
  )
  return mapConversation(data)
}

export async function replyToConversation(workspaceId, botId, id, body, opts = {}) {
  const updated = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/conversations/${id}/reply`,
    {
      method: 'POST',
      body: JSON.stringify({
        body: body.trim(),
        agentName: opts.agentName,
        ...(opts.assigneeId ? { assigneeId: String(opts.assigneeId) } : {}),
      }),
    },
  )
  return mapConversation(updated)
}

export async function assignConversation(workspaceId, botId, id, opts = {}) {
  const body = {}
  if (opts.agentName) body.agentName = opts.agentName
  if (opts.assigneeId) body.assigneeId = opts.assigneeId
  const updated = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/conversations/${id}/assign`,
    { method: 'POST', body: JSON.stringify(body) },
  )
  return mapConversation(updated)
}

/** Return conversation to the bot (aiPaused off) and post a context-summary resume message. */
export async function assignConversationToBot(workspaceId, botId, id) {
  const updated = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/conversations/${id}/assign-to-bot`,
    { method: 'POST', body: JSON.stringify({}) },
  )
  return mapConversation(updated)
}

export async function setConversationStatus(workspaceId, botId, id, status) {
  const updated = await chatbotFetch(
    `${wsBase(workspaceId)}/bots/${botId}/conversations/${id}/status`,
    {
      method: 'POST',
      body: JSON.stringify({ status }),
    },
  )
  return mapConversation(updated)
}

export async function deleteConversation(workspaceId, botId, id) {
  await chatbotFetch(`${wsBase(workspaceId)}/bots/${botId}/conversations/${id}`, {
    method: 'DELETE',
  })
  return { success: true }
}
